import { useEffect, useMemo, useState } from "react"
import { useCarriageLabelServices, useCarriageLabels, type LabelQuery } from "./useCarriageLabels"
import type { CarriageLabelService } from "./carriageLabelsTypes"
import { buildCarriageLabelsPdf } from "./carriageLabelPdf"
import { downloadPdf, previewPdf } from "./pdfBuilder"
import { CustomCarriageLabelDialog } from "./CustomCarriageLabelDialog"
import { CarriageLabelEditorDialog } from "./CarriageLabelEditorDialog"

// Self-contained "Carriage Labels" flow for the Reports A La Carte modal. Holds
// all its own state so the menu-card flows are untouched. Three ways to pick what
// to print — a service, a date range, or a search across every label field — then
// Preview / Generate the A5 window labels as SVG->PDF.
//
// Search results are clickable: clicking one opens the label editor with the
// whole result set, so labels can be corrected (and the fix saved) before print.

function isoOffset(days: number): string {
  const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10)
}
function fmtDate(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso + "T00:00:00")
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
}
function tidyRoute(r: string): string { return (r || "").replace(/\s+/g, " ").trim() }

type Mode = "service" | "range" | "search"

export function CarriageLabelsPane({ open }: { open: boolean }) {
  const [mode, setMode] = useState<Mode>("service")
  const [from, setFrom] = useState(isoOffset(0))
  const [to, setTo] = useState(isoOffset(30)) // default view: a month ahead (matches the cache window)
  const [selected, setSelected] = useState<CarriageLabelService | null>(null)
  const [q, setQ] = useState("")
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)
  const [customOpen, setCustomOpen] = useState(false)
  const [editorAt, setEditorAt] = useState<number | null>(null)
  const [doubleSided, setDoubleSided] = useState(false)

  useEffect(() => {
    if (open) {
      setSelected(null); setQ(""); setGenError(null); setProgress(null)
      setCustomOpen(false); setEditorAt(null); setDoubleSided(false)
    }
  }, [open])
  useEffect(() => { setSelected(null); setEditorAt(null) }, [mode])

  const services = useCarriageLabelServices(from, to, open && mode === "service")

  const labelQuery: LabelQuery = useMemo(() => {
    if (mode === "service") {
      return selected ? { date: selected.date ?? "", time: selected.time, route: selected.route } : {}
    }
    // Search spans the whole cached window — the point is to find a label by any
    // of its fields, not to hunt for the right date range first.
    if (mode === "search") return { q }
    return { from, to, q }
  }, [mode, selected, from, to, q])
  const labelsEnabled =
    open && (mode === "range" || (mode === "search" && q.trim() !== "") || (mode === "service" && !!selected))
  const labels = useCarriageLabels(labelQuery, labelsEnabled)

  const rows = labels.data?.rows ?? []
  const total = labels.data?.total ?? rows.length
  const busy = generating
  // Bound the date pickers to what's actually cached (defaults to a month ahead).
  const winFrom = services.data?.window_from ?? labels.data?.window_from ?? undefined
  const winTo = services.data?.window_to ?? labels.data?.window_to ?? undefined

  const onPreview = async () => {
    if (rows.length === 0) return
    setGenError(null)
    try {
      const res = await buildCarriageLabelsPdf({ rows: rows.slice(0, 1) })
      previewPdf(res.blob, "carriage-label-preview.pdf")
    } catch (err) { setGenError(err instanceof Error ? err.message : String(err)) }
  }

  const onGenerate = async () => {
    if (rows.length === 0) { setGenError("No labels to print for this selection."); return }
    setGenerating(true); setGenError(null); setProgress({ current: 0, total: rows.length })
    try {
      const res = await buildCarriageLabelsPdf({ rows, doubleSided, onProgress: (current, t) => setProgress({ current, total: t }) })
      const stub = mode === "service" && selected
        ? `${selected.date}-${selected.time.replace(":", "")}`
        : mode === "search"
          ? `search-${q.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30) || "all"}`
          : `${from}_to_${to}`
      downloadPdf(res.blob, `carriage-labels-${stub}.pdf`)
      if (res.failed.length > 0) setGenError(`${res.failed.length} label(s) failed to render.`)
    } catch (err) { setGenError(err instanceof Error ? err.message : String(err)) }
    finally { setGenerating(false); setProgress(null) }
  }

  const serviceRows = services.data?.rows ?? []
  const isSel = (s: CarriageLabelService) => !!selected && selected.date === s.date && selected.time === s.time && selected.route === s.route

  return (
    <>
      <div className="alacarte-options-head alacarte-options-head--split">
        <div className="alacarte-section-title">Carriage labels</div>
        <button type="button" className="btn btn--ghost cl-custom-open" onClick={() => setCustomOpen(true)}
          title="Free-type a one-off label — a late charter, a private hire, anything not in MERAC">
          Create custom label
        </button>
      </div>

      <div className="alacarte-modetoggle" role="radiogroup" aria-label="How to choose">
        <button type="button" role="radio" aria-checked={mode === "service"}
          className={"alacarte-modetoggle-btn" + (mode === "service" ? " alacarte-modetoggle-btn--active" : "")}
          onClick={() => setMode("service")}>
          <span className="alacarte-modetoggle-icon" aria-hidden="true">📋</span>
          <span className="alacarte-modetoggle-label">A service</span>
          <span className="alacarte-modetoggle-blurb">Pick one service &mdash; print all its group labels</span>
        </button>
        <button type="button" role="radio" aria-checked={mode === "range"}
          className={"alacarte-modetoggle-btn" + (mode === "range" ? " alacarte-modetoggle-btn--active" : "")}
          onClick={() => setMode("range")}>
          <span className="alacarte-modetoggle-icon" aria-hidden="true">🗓️</span>
          <span className="alacarte-modetoggle-label">A date range</span>
          <span className="alacarte-modetoggle-blurb">Every group label across a span of dates</span>
        </button>
        <button type="button" role="radio" aria-checked={mode === "search"}
          className={"alacarte-modetoggle-btn" + (mode === "search" ? " alacarte-modetoggle-btn--active" : "")}
          onClick={() => setMode("search")}>
          <span className="alacarte-modetoggle-icon" aria-hidden="true">🔍</span>
          <span className="alacarte-modetoggle-label">Search &amp; edit</span>
          <span className="alacarte-modetoggle-blurb">Find labels by any field, then click to edit</span>
        </button>
      </div>

      <div className="alacarte-section">
        {mode !== "search" && (
          <div className="alacarte-row">
            <label className="alacarte-field"><span>From</span>
              <input type="date" value={from} min={winFrom} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="alacarte-field"><span>To</span>
              <input type="date" value={to} min={from} max={winTo} onChange={(e) => setTo(e.target.value)} /></label>
            {labels.data?.window_from && labels.data?.window_to && (
              <span className="alacarte-row-meta">Cached {fmtDate(labels.data.window_from)} &ndash; {fmtDate(labels.data.window_to)}</span>
            )}
          </div>
        )}

        {mode === "service" ? (
          <>
            {services.error && <div className="alacarte-empty alacarte-empty--error">Couldn&apos;t load services: {services.error}</div>}
            {!services.error && (services.data?.directory_rows ?? 0) === 0 && !services.loading && (
              <div className="alacarte-empty"><strong>Awaiting first refresh.</strong> Run <code>Refresh-CarriageLabels.ps1</code> on ONTRACK to populate carriage labels.</div>
            )}
            <div className="alacarte-services">
              {services.loading && <div className="alacarte-empty">Loading services&hellip;</div>}
              {!services.loading && serviceRows.length === 0 && (services.data?.directory_rows ?? 0) > 0 && (
                <div className="alacarte-empty">No group carriage labels in {fmtDate(from)} &ndash; {fmtDate(to)}.</div>
              )}
              {serviceRows.map((s) => (
                <button type="button" key={`${s.date}|${s.time}|${s.route}`}
                  className={"alacarte-service" + (isSel(s) ? " alacarte-service--selected" : "")}
                  onClick={() => setSelected(s)}>
                  <span className="alacarte-service-line1">{fmtDate(s.date)} &middot; {s.time}</span>
                  <span className="alacarte-service-line2">{tidyRoute(s.route)} &middot; {s.labels} label{s.labels === 1 ? "" : "s"} &middot; {s.groups} group{s.groups === 1 ? "" : "s"}</span>
                </button>
              ))}
            </div>
          </>
        ) : mode === "search" ? (
          <>
            <label className="alacarte-search">
              <span className="alacarte-search-icon" aria-hidden="true">🔍</span>
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                placeholder="Group, route, booking ref, time, date, party size&hellip;" />
              {q && <button type="button" className="alacarte-search-clear" onClick={() => setQ("")} aria-label="Clear search">×</button>}
            </label>
            <p className="cl-search-hint">
              Searches every field on every cached label. Click a result to open it in the editor
              {rows.length > 1 ? " with the whole result set" : ""}.
            </p>
            <div className="alacarte-services cl-search-results">
              {q.trim() === "" && <div className="alacarte-empty">Type something to search the cached labels.</div>}
              {q.trim() !== "" && labels.loading && <div className="alacarte-empty">Searching&hellip;</div>}
              {q.trim() !== "" && !labels.loading && rows.length === 0 && !labels.error && (
                <div className="alacarte-empty">No labels match &ldquo;{q}&rdquo;.</div>
              )}
              {rows.map((r, i) => (
                <button type="button" key={`${r.BookingKey ?? ""}|${r.JourneyDate ?? ""}|${r.TimeOutKey ?? ""}|${r.LabelNo ?? ""}|${i}`}
                  className="alacarte-service cl-search-result"
                  onClick={() => setEditorAt(i)}
                  title="Open in the label editor">
                  <span className="alacarte-service-line1">
                    {r.Account || "(no name)"}
                    {r.Edited && <span className="cl-editor-badge">edited</span>}
                  </span>
                  <span className="alacarte-service-line2">
                    {fmtDate(r.JourneyDate)} &middot; {r.TimeOut} &middot; {tidyRoute(r.OutFrom)}
                    {r.LabelNo != null && r.OfLabels != null ? ` · Label ${r.LabelNo} of ${r.OfLabels}` : ""}
                    {r.Booking ? ` · Booking ${r.Booking}` : ""}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <label className="alacarte-search">
            <span className="alacarte-search-icon" aria-hidden="true">🔍</span>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by group name, route or booking ref&hellip;" />
            {q && <button type="button" className="alacarte-search-clear" onClick={() => setQ("")} aria-label="Clear search">×</button>}
          </label>
        )}
      </div>

      <footer className="alacarte-footer">
        <div className="alacarte-footer-summary">
          {genError
            ? <span className="alacarte-footer-error">{genError}</span>
            : labels.error
              ? <span className="alacarte-footer-error">{labels.error}</span>
              : progress
                ? <span>Rendering {progress.current} / {progress.total}&hellip;</span>
                : labelsEnabled
                  ? <span>{labels.loading ? "Loading labels…" : `${total} label${total === 1 ? "" : "s"} ready${total > rows.length ? ` (showing first ${rows.length})` : ""}`}</span>
                  : <span className="alacarte-footer-hint">
                      {mode === "search" ? "Search for a label to edit or print it." : "Pick a service to print its carriage labels."}
                    </span>}
        </div>
        <label className="cl-doublesided" title="Print each label twice, back-to-back, so a duplex printer puts both copies on one sheet">
          <input type="checkbox" checked={doubleSided} disabled={busy} onChange={(e) => setDoubleSided(e.target.checked)} />
          <span>Print double sided</span>
        </label>
        <div className="alacarte-footer-actions">
          <button type="button" className="btn btn--ghost" onClick={() => void onPreview()} disabled={busy || rows.length === 0}
            title="Render the first label and open it in a new tab">Preview</button>
          <button type="button" className="btn btn--primary" onClick={() => void onGenerate()} disabled={busy || rows.length === 0}>
            {busy
              ? "Generating…"
              : `Generate ${rows.length || ""} label${rows.length === 1 ? "" : "s"}${doubleSided && rows.length > 0 ? ` ×2 (${rows.length * 2} pages)` : ""} (A5 PDF)`}
          </button>
        </div>
      </footer>

      <CustomCarriageLabelDialog open={customOpen} onClose={() => setCustomOpen(false)} />
      <CarriageLabelEditorDialog
        open={editorAt !== null}
        rows={rows}
        initialIndex={editorAt ?? 0}
        onClose={() => setEditorAt(null)}
        onSaved={labels.reload}
      />
    </>
  )
}
